/**
 * Phase 13b — client-side aggregate id generator.
 *
 * Phase 9f settled that aggregate ids are client-generated (so
 * concurrent offline creates from two devices don't collide on a
 * server-side counter). Per `Progress.md` Phase 12d, the smart
 * factories re-validate non-emptiness on the decode path; this
 * helper supplies the actual value.
 *
 * The shape is `<prefix>_<base36-time>_<base36-random>` —
 * lexicographically sortable by creation time (handy for stable list
 * ordering before a server-side `createdAt` is observed) and
 * compact enough to fit in URL params.
 *
 * The `randomSource` and `clockSource` parameters are seams so the
 * tests don't depend on a real `Math.random` / `Date.now`.
 */

export type RandomSource = () => number;
export type ClockSource = () => number;

export type IdGeneratorDeps = {
  readonly random?: RandomSource;
  readonly clock?: ClockSource;
};

const DEFAULT_RANDOM: RandomSource = () => Math.random();
const DEFAULT_CLOCK: ClockSource = () => Date.now();

export const generateId = (
  prefix: string,
  deps: IdGeneratorDeps = {},
): string => {
  if (prefix.length === 0) {
    throw new Error("generateId: prefix must be a non-empty string");
  }
  const clock = (deps.clock ?? DEFAULT_CLOCK)();
  const random = (deps.random ?? DEFAULT_RANDOM)();
  const timePart = Math.floor(clock).toString(36);
  // 9 random base36 chars ≈ 46 bits of entropy — sufficient at the
  // client scale (a user creating thousands of ids has bigger
  // problems than collisions).
  const randPart = Math.floor(Math.abs(random) * 0xFFFFFFFFFF)
    .toString(36)
    .padStart(9, "0");
  return `${prefix}_${timePart}_${randPart}`;
};
